import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PortfolioItem } from "@/lib/content-client";

type PortfolioLightboxProps = {
  items: PortfolioItem[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
};

/**
 * Viewer for database-backed imagery.
 *
 * Deliberately separate from the home page's `Lightbox`: that one derives
 * responsive `srcset` variants from the build-time photo manifest in
 * `lib/photos.ts`, which cannot describe an R2 object whose key is only known
 * at runtime. This one takes the resolved URL directly, and adds the caption
 * the portfolio needs.
 */
export function PortfolioLightbox({ items, index, onClose, onIndexChange }: PortfolioLightboxProps) {
  const open = index !== null;
  const [loaded, setLoaded] = useState(false);
  const touchX = useRef<number | null>(null);

  const item = index !== null ? items[index] : null;

  const go = useCallback(
    (delta: number) => {
      if (index === null || items.length === 0) return;
      onIndexChange((index + delta + items.length) % items.length);
    },
    [index, items.length, onIndexChange],
  );

  // Reset the fade for each newly-shown image.
  useEffect(() => setLoaded(false), [index]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    // Stop the page behind the overlay from scrolling on wheel/touch.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, go, onClose]);

  if (!open || !item) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
      className="fixed inset-0 z-50 flex flex-col bg-espresso/97 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-5 right-5 z-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
      >
        <X className="h-5 w-5" strokeWidth={1.5} />
      </button>

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              go(-1);
            }}
            aria-label="Previous piece"
            className="absolute top-1/2 left-3 z-10 flex h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors duration-300 hover:border-gold hover:text-gold sm:left-6"
          >
            <ChevronLeft className="h-6 w-6" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              go(1);
            }}
            aria-label="Next piece"
            className="absolute top-1/2 right-3 z-10 flex h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors duration-300 hover:border-gold hover:text-gold sm:right-6"
          >
            <ChevronRight className="h-6 w-6" strokeWidth={1.5} />
          </button>
        </>
      )}

      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-16 sm:px-20"
        onClick={e => e.stopPropagation()}
        onTouchStart={e => {
          touchX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={e => {
          const start = touchX.current;
          const end = e.changedTouches[0]?.clientX ?? null;
          touchX.current = null;
          if (start === null || end === null) return;
          const delta = end - start;
          // 45px threshold keeps a stray tap from flipping the image.
          if (Math.abs(delta) > 45) go(delta < 0 ? 1 : -1);
        }}
      >
        <img
          key={item.id}
          src={item.src}
          alt={item.alt}
          onLoad={() => setLoaded(true)}
          className={cn(
            "max-h-full max-w-full object-contain transition-opacity duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      </div>

      <div
        className="shrink-0 px-6 pb-10 text-center sm:px-20"
        onClick={e => e.stopPropagation()}
      >
        {item.category && (
          <p className="font-sans text-[0.5625rem] tracking-[0.3em] text-gold uppercase">
            {item.category}
          </p>
        )}
        <h2 className="mt-3 font-display text-2xl font-light text-ivory sm:text-3xl">
          {item.title}
        </h2>
        {item.description && (
          <p className="mx-auto mt-3 max-w-xl font-sans text-sm leading-relaxed font-light text-ivory/60">
            {item.description}
          </p>
        )}
        {items.length > 1 && (
          <p className="mt-5 font-sans text-[0.625rem] tracking-[0.24em] text-ivory/40 uppercase">
            {index + 1} / {items.length}
          </p>
        )}
      </div>
    </div>
  );
}
